import axios, { AxiosResponse } from "axios";
import {
  AI_CHAT_URL,
  AI_CREATE_SESSION_URL,
  AI_EXAM_URL
} from "@/store/urls/ai_assessment";

export interface CreateSessionPayload {
  assessment_type: string;
  topic: string;
  difficulty: string;
  short_answer_count: number;
  mcq_count: number;
  true_false_count: number;
}

export interface QuestionOption {
  id?: string | number;
  text: string;
  options?: string[];
  correct_answer?: string | number | boolean;
  type?: string;
}

export interface CreateSessionResponse {
  session_id: string;
  status: string;
  message: string;
  current_step: string;
  assessment_data: null;
}

export const ai_create_session = async (): Promise<
  AxiosResponse<CreateSessionResponse>
> => await axios.post(AI_CREATE_SESSION_URL);
export interface ChatPayload {
  message: string;
  session_id: string;
}

export interface ChatResponse {
  session_id: string;
  status: string;
  message: string;
  current_step: string;
  assessment_data?: {
    total_questions: number;
    short_answer_count: number;
    mcq_count: number;
    true_false_count: number;
    questions: QuestionOption[];
  };
}

export const ai_chat = async (
  payload: ChatPayload,
  onMessage?: (message: string) => void,
  onComplete?: (data: ChatResponse) => void,
  onError?: (error: Error) => void,
  onProgress?: (progress: number) => void
): Promise<void> => {
  try {
    const response = await fetch(AI_CHAT_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (!response.body) {
      throw new Error("No response body");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    // Get content length for progress calculation
    const contentLength = response.headers.get("content-length");

    const totalBytes = contentLength ? parseInt(contentLength, 10) : null;
    let receivedBytes = 0;

    try {
      let done = false;
      while (!done) {
        const result = await reader.read();
        done = result.done;
        const value = result.value;

        if (done) {
          // Set progress to 100% when complete
          onProgress?.(100);
          break;
        }

        // Update received bytes for progress calculation
        receivedBytes += value?.length || 0;
        // Calculate progress percentage
        if (totalBytes && totalBytes > 0) {
          const progress = Math.min(
            100,
            Math.round((receivedBytes / totalBytes) * 100)
          );
          onProgress?.(progress);
        } else {
          // If no content-length, estimate progress based on chunks received
          // This is a rough estimation - you might want to adjust this logic
          const estimatedProgress = Math.min(95, receivedBytes / 100000); // Rough estimate
          onProgress?.(Math.round(estimatedProgress));
        }

        // Decode the chunk immediately
        const chunk = decoder.decode(value, { stream: true });

        // Process the chunk immediately for real-time streaming
        if (chunk.trim()) {
          // Split by lines to handle multiple JSON objects in one chunk
          const lines = chunk.split("\n").filter((line) => line.trim());

          for (const line of lines) {
            try {
              // Handle Server-Sent Events format with "data: " prefix
              let jsonString = line;
              if (line.startsWith("data: ")) {
                jsonString = line.replace(/^data:\s*/, "");
              }

              // Skip empty lines
              if (!jsonString.trim()) continue;

              // Try to parse as JSON
              const data = JSON.parse(jsonString);

              if (data.type === "token" && data.content) {
                // Handle token format: {"content": "text", "type": "token"}
                onMessage?.(data.content);
              } else if (data.type === "done") {
                // Handle completion: {"content": "", "type": "done"}
                onComplete?.(data);
                return;
              } else if (data.type === "message" && data.content) {
                onMessage?.(data.content);
              } else if (data.type === "complete") {
                onComplete?.(data);
                return;
              } else if (data.type === "error") {
                onError?.(data.error);
                return;
              } else if (data.content) {
                // Direct content without type
                onMessage?.(data.content);
              }
            } catch (jsonError) {
              // If not JSON, treat as plain text content
              onComplete?.({
                message: "Error parsing line",
                session_id: "",
                status: "",
                current_step: ""
              });
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  } catch (error) {
    console.error("Streaming error:", error);
    onError?.(error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
};

// Fetch exam data by session ID
export const fetch_exam_data = async (
  sessionId: string
): Promise<ChatResponse> => {
  try {
    const response: AxiosResponse<ChatResponse> = await axios.get(
      AI_EXAM_URL + `/${sessionId}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching exam data:", error);
    throw error;
  }
};
